import { useCallback, useEffect, useRef, useState } from 'react';
import type { Report, ServerReport, Signals, SignalModuleId } from '@fraudeai/schema';
import { collect, computeIdentity, rememberVisit } from '@fraudeai/collector';
import { buildReport } from '@fraudeai/rules';
import { API_BASE, ApiUnavailableError, fetchReport, shareUrl, submitReport } from './api.js';
import { Verdict } from './components/Verdict.js';
import { AddressCard } from './components/AddressCard.js';
import { Findings } from './components/Findings.js';
import { Details } from './components/Details.js';
import { ServerPanel } from './components/ServerPanel.js';

interface Progress {
  done: number;
  total: number;
  current: SignalModuleId | null;
}

interface AdvancedOptions {
  media: boolean;
  geolocation: boolean;
}

const DEFAULT_ADVANCED: AdvancedOptions = { media: false, geolocation: false };

/**
 * Три состояния отчёта, и разница между ними существенна.
 *
 * `server` — сервер пересчитал всё сам и добавил сетевой слой. Это
 * авторитетный результат, именно его показываем как итог.
 *
 * `local` — сервер недоступен, показываем измерения браузера. Такой результат
 * заведомо оптимистичен: сетевой слой не проверялся вовсе, и об этом сказано
 * прямо, а не мелким шрифтом.
 *
 * `shared` — открыт чужой сохранённый отчёт, ничего не измеряем.
 */
type View =
  | { kind: 'scanning'; progress: Progress }
  | { kind: 'server'; report: ServerReport }
  | { kind: 'local'; report: Report; reason: string }
  | { kind: 'shared'; report: ServerReport }
  | { kind: 'error'; message: string };

const MODULE_TITLES: Partial<Record<SignalModuleId, string>> = {
  navigator: 'читаем данные о браузере',
  screen: 'меряем экран',
  canvas: 'рисуем и сверяем повторные отрисовки',
  webgl: 'опрашиваем видеокарту',
  webgpu: 'запрашиваем адаптер WebGPU',
  audio: 'считаем аудио-отпечаток',
  fonts: 'ищем установленные шрифты',
  rects: 'меряем геометрию элементов',
  contexts: 'сравниваем воркеры и фреймы',
  natives: 'проверяем целостность функций',
  engine: 'снимаем отпечаток движка JS',
  automation: 'ищем следы автоматизации',
  os: 'собираем маркеры системы',
  locale: 'сверяем время и локаль',
  storage: 'проверяем хранилища',
  media: 'сверяем медиа-устройства',
  geolocation: 'проверяем геолокацию',
  permissions: 'читаем разрешения',
  network: 'опрашиваем WebRTC',
  probes: 'запускаем пробы по UDP и DNS',
};

function sharedReportId(): string | null {
  return new URLSearchParams(location.search).get('report');
}

export function App() {
  const [view, setView] = useState<View>({
    kind: 'scanning',
    progress: { done: 0, total: 1, current: null },
  });
  const running = useRef(false);
  const advancedRef = useRef<AdvancedOptions>(DEFAULT_ADVANCED);
  const [advanced, setAdvanced] = useState<AdvancedOptions>(DEFAULT_ADVANCED);

  const scan = useCallback(async (requested?: AdvancedOptions) => {
    // StrictMode в разработке вызывает эффекты дважды — защищаемся, иначе
    // одновременно поднимутся два набора воркеров и фреймов.
    if (running.current) return;
    running.current = true;
    const selected = requested ?? advancedRef.current;

    setView({ kind: 'scanning', progress: { done: 0, total: 1, current: null } });

    try {
      const result = await collect({
        serviceWorkerUrl: '/fraudeai-probe-sw.js',
        // Модуль внешних проб просит у сервера сессию и адреса STUN,
        // HTTP/3 и зоны DNS. Без этого пробы не запускаются.
        apiBaseUrl: API_BASE,
        requestMediaProbe: selected.media,
        requestGeolocationProbe: selected.geolocation,
        onProgress: (done, total, current) =>
          setView({ kind: 'scanning', progress: { done, total, current } }),
      });

      const identity = computeIdentity(result.signals);
      rememberVisit(identity.visitorId);

      try {
        const report = await submitReport(result, identity);
        // Ссылка появляется в адресной строке без перезагрузки: отчёт
        // становится ссылкой сразу, а не после отдельного действия.
        history.replaceState(null, '', `?report=${report.id}`);
        setView({ kind: 'server', report });
      } catch (cause) {
        const reason =
          cause instanceof ApiUnavailableError
            ? 'сервер не отвечает'
            : cause instanceof Error
              ? cause.message
              : String(cause);
        // networkLayerChecked: false — сетевые правила не отрабатывали, и
        // оценка обязана это отражать потолком, а не выглядеть как вердикт.
        setView({
          kind: 'local',
          report: buildReport(result, { identity, networkLayerChecked: false }),
          reason,
        });
      }
    } catch (cause) {
      setView({
        kind: 'error',
        message: cause instanceof Error ? cause.message : String(cause),
      });
    } finally {
      running.current = false;
    }
  }, []);

  const openShared = useCallback(async (id: string) => {
    try {
      setView({ kind: 'shared', report: await fetchReport(id) });
    } catch (cause) {
      setView({
        kind: 'error',
        message:
          cause instanceof ApiUnavailableError
            ? 'сервер не отвечает, сохранённый отчёт открыть не удалось'
            : cause instanceof Error
              ? cause.message
              : String(cause),
      });
    }
  }, []);

  useEffect(() => {
    const id = sharedReportId();
    if (id) {
      void openShared(id);
    } else {
      void scan();
    }
  }, [scan, openShared]);

  const rescan = () => {
    history.replaceState(null, '', location.pathname);
    void scan(advancedRef.current);
  };

  const runAdvanced = (next: AdvancedOptions) => {
    advancedRef.current = next;
    setAdvanced(next);
    history.replaceState(null, '', location.pathname);
    void scan(next);
  };

  const report = view.kind === 'server' || view.kind === 'shared' ? view.report : null;
  const signals = (report?.signals ?? {}) as Partial<Signals>;

  return (
    <div className="shell">
      <header className="masthead">
        <div>
          <h1>
            fraude<span>AI</span>
          </h1>
          <p>
            Открытый чекер окружения. Проверяет не отдельные значения, а согласованность
            слоёв между собой — и объясняет каждое срабатывание.
          </p>
        </div>
        {view.kind !== 'scanning' && (
          <div className="masthead-actions">
            {!advanced.media && (
              <button className="ghost" onClick={() => runAdvanced({ ...advanced, media: true })}>
                Проверить медиа
              </button>
            )}
            {!advanced.geolocation && (
              <button
                className="ghost"
                onClick={() => runAdvanced({ ...advanced, geolocation: true })}
              >
                Проверить GPS
              </button>
            )}
            <button className="ghost" onClick={rescan}>
              {view.kind === 'shared' ? 'Проверить себя' : 'Проверить заново'}
            </button>
          </div>
        )}
      </header>

      {view.kind === 'scanning' && (
        <div className="scanner">
          <div className="scanner-bar">
            <div
              style={{ width: `${Math.round((view.progress.done / view.progress.total) * 100)}%` }}
            />
          </div>
          <div className="scanner-label">
            {view.progress.current
              ? (MODULE_TITLES[view.progress.current] ?? view.progress.current)
              : 'запускаем проверки'}
            {' · '}
            {view.progress.done}/{view.progress.total}
          </div>
        </div>
      )}

      {view.kind === 'error' && (
        <div className="scanner">
          <p style={{ color: 'var(--fraudulent)' }}>Проверка не выполнилась: {view.message}</p>
          <button className="primary" onClick={rescan}>
            Попробовать снова
          </button>
        </div>
      )}

      {view.kind === 'shared' && (
        <div className="notice">
          Открыт сохранённый отчёт от {new Date(view.report.createdAt).toLocaleString('ru-RU')}.
          Это чужие измерения — ваше окружение сейчас не проверяется.
        </div>
      )}

      {view.kind === 'local' && (
        <div className="notice">
          <strong>Проверен только браузерный слой.</strong> Отправить измерения на сервер
          не удалось ({view.reason}), поэтому сетевые проверки — отпечаток TLS, отпечаток
          HTTP/2, сверка User-Agent из заголовка запроса, порядок заголовков — не
          выполнялись. Именно они и ловят антидетект-браузеры: те бывают безупречно
          согласованы внутри страницы и выдают себя только сетевым стеком. Поэтому
          оценка ниже ограничена сверху и вердиктом не является.
        </div>
      )}

      {report && (
        <>
          <Verdict
            score={report.score}
            identity={report.identity ?? { visitorId: '—', confidence: 0, components: [] }}
          />
          <AddressCard server={report.server} />
          <Findings findings={report.findings} />
          <ServerPanel server={report.server} />
          <Details modules={report.modules} signals={signals} />

          <section>
            <h3>Ссылка на отчёт</h3>
            <p className="hint">
              {/* Ноль в expiresAt означает «бессрочно», а не «уже истёк»: так
                  хранятся помеченные прогоны. Без этой развилки вычитание
                  давало минус полмиллиона часов. */}
              {report.expiresAt > 0
                ? `Отчёт хранится на сервере ${Math.round(
                    (report.expiresAt - report.createdAt) / 3600000,
                  )} ч.`
                : 'Прогон помечен, поэтому хранится бессрочно — пока его не удалят вручную.'}{' '}
              Доступен по ссылке всем, у кого она есть.
            </p>
            <div className="share">
              <code>{shareUrl(report.id)}</code>
              <button
                className="ghost"
                onClick={() => void navigator.clipboard?.writeText(shareUrl(report.id))}
              >
                Скопировать
              </button>
            </div>
          </section>
        </>
      )}

      {view.kind === 'local' && (
        <>
          <Verdict score={view.report.score} identity={view.report.identity} />
          {/* Карточки адреса здесь нет намеренно: этот отчёт снят без
              сервера, и адрес, с которого нас видят, неизвестен. */}
          <Findings findings={view.report.findings} />
          <Details modules={view.report.modules} signals={view.report.signals} />
        </>
      )}

      <footer>
        <p>
          Проверка идёт четырьмя независимыми слоями. Браузер измеряет сам себя,
          включая опрос воркеров и фреймов. Сервер пересчитывает всё заново по
          сырым измерениям — выводам страницы он не доверяет, иначе достаточно
          было бы прислать пустой список находок. Терминатор TLS снимает
          отпечатки рукопожатия и кадров HTTP/2, до которых со страницы не
          дотянуться ни через JavaScript, ни через расширение. Внешние пробы
          проверяют, одним ли путём идут TCP, UDP и DNS.
        </p>
        <p>
          Вердикт строится не на отдельных подозрительных значениях, а на
          противоречиях между слоями: они задаются независимо друг от друга и
          потому не могут расходиться у настоящего браузера. Если какой-то слой
          проверить не удалось, это видно в отчёте отдельно — «не нашли проблем»
          и «не смогли проверить» здесь разные вещи.
        </p>
      </footer>
    </div>
  );
}
